import { create } from 'zustand';

/**
 * What the user wanted to *say* about a piece of context, written when they attached it.
 *
 * Every kind of context carries one, because attaching alone only ever says "look here" —
 * it can't say "this is the one I meant", "this line is wrong", or, against a question the
 * agent asked, the answer. Empty when they didn't write anything.
 */
interface Annotated {
  note: string;
}

/** One element picked in the Preview tab, as reported by the injected agent. */
export interface ElementSelection extends Annotated {
  kind: 'element';
  /** Client-side id — the payload carries nothing unique to key React on. */
  id: string;
  /** `querySelector`-able, and deliberately structural: see `preview_select.js`. */
  selector: string;
  /** Human-readable ancestor chain, for the agent rather than for the DOM. */
  path: string;
  label: string;
  tagName: string;
  text: string;
  snippet: string;
  /** The element's own attributes, serialized — usually where the props surface. */
  attributes: string;
  /** React owner chain, innermost first. Empty for a non-React or production page. */
  componentStack: string[];
  component: string | null;
  /** `file:line:column` from React's dev build, when it still carries one. */
  source: string | null;
  rect: { x: number; y: number; width: number; height: number };
  /** The previewed app's own route (`/login`), not the proxy path serving it. */
  route: string;
}

/** One span of source picked in a file viewer or diff, quoted straight from the file it lives in. */
export interface CodeSelection extends Annotated {
  kind: 'code';
  id: string;
  path: string;
  startLine: number;
  endLine: number;
  snippet: string;
  label: string;
}

/** A whole file referenced by path, picked from the file tree or a tab rather than out of a
 * selection within the file's own text. Carries no content — the agent has full sandbox access
 * and reads the file itself; only a deliberately selected snippet needs to travel with its text. */
export interface FileSelection extends Annotated {
  kind: 'file';
  id: string;
  path: string;
  label: string;
}

/** A slice of an earlier turn in this same thread, picked out of the rendered message.
 *
 * The point is steering: quoting the exact question the agent asked (or the exact sentence of
 * one's own earlier prompt) and replying to *that*, instead of hoping a bare "yes, the second
 * one" lands on the right paragraph twenty turns back. */
export interface QuoteSelection extends Annotated {
  kind: 'quote';
  id: string;
  /** Who wrote the turn the slice came from — the agent has to know whether it is being
   * shown its own words or the user's. */
  role: 'user' | 'assistant';
  snippet: string;
  label: string;
}

/** A passage of the plan document the user is commenting on.
 *
 * The version travels with it because the plan is rewritten wholesale between versions: by the
 * time the agent reads the message it may already have saved a newer one, and "the passage that
 * said X in v3" is findable where a bare quote from a document that no longer exists is not. */
export interface PlanSelection extends Annotated {
  kind: 'plan';
  id: string;
  version: number;
  snippet: string;
  label: string;
}

export type Selection = ElementSelection | CodeSelection | FileSelection | QuoteSelection | PlanSelection;

/** The wire shape, before an id is attached. The note is optional at the door — the file tree
 * attaches from a context menu with nowhere to type one, and it can be written later. */
type Unstaged<T extends Annotated> = Omit<T, 'note'> & { note?: string };

export type ElementSelectionPayload = Unstaged<Omit<ElementSelection, 'id' | 'kind'>>;
export type CodeSelectionPayload = Unstaged<Omit<CodeSelection, 'id' | 'kind' | 'label'>>;
export type FileSelectionPayload = Unstaged<Omit<FileSelection, 'id' | 'kind' | 'label'>>;
export type QuoteSelectionPayload = Unstaged<Omit<QuoteSelection, 'id' | 'kind' | 'label'>>;
export type PlanSelectionPayload = Unstaged<Omit<PlanSelection, 'id' | 'kind' | 'label'>>;

interface SelectionState {
  selections: Selection[];
  /**
   * The staged selection the composer should open a note field on, focused.
   *
   * Set by an attach gesture that asked to write about what it attached — the selection toolbar's
   * note button — so the note gets typed at the composer, beside the rest of the message, instead
   * of in a form covering the passage it is about. One-shot: the chip clears it as it opens.
   */
  annotating: string | null;

  /** Each `add` takes `annotate` — whether the gesture also asked to write about it. */
  addElement: (selection: ElementSelectionPayload, annotate?: boolean) => void;
  addCode: (selection: CodeSelectionPayload, annotate?: boolean) => void;
  addFile: (selection: FileSelectionPayload, annotate?: boolean) => void;
  addQuote: (selection: QuoteSelectionPayload, annotate?: boolean) => void;
  addPlan: (selection: PlanSelectionPayload, annotate?: boolean) => void;
  /** Write (or clear) the note on something already staged. */
  setNote: (id: string, note: string) => void;
  setAnnotating: (id: string | null) => void;
  remove: (id: string) => void;
  clear: () => void;
}

function codeLabel(path: string, startLine: number, endLine: number): string {
  const name = path.split('/').pop() ?? path;
  return startLine === endLine ? `${name}:${startLine}` : `${name}:${startLine}-${endLine}`;
}

const QUOTE_LABEL_MAX = 32;

/** Line-leading markdown — heading hashes, bullets, checkboxes, quote carets. It is syntax the
 *  user never saw in the rendered document the passage was picked out of, so a label that keeps
 *  it ("## Optional but useful MVP…") names the passage by characters they don't recognise. */
const LINE_MARKUP = /^\s*(?:#{1,6}\s+|>\s*|(?:[-*+]|\d+[.)])\s+(?:\[[ xX]\]\s+)?)/;

/** A quote has no path or line to name itself by, so the chip shows the opening words —
 * flattened to one line, since a chip is one line tall whatever the selection spanned, and
 * cut at a word boundary rather than mid-word when there is a usable one to cut at. */
function quoteLabel(snippet: string): string {
  const flat = snippet
    .split('\n')
    .map((line) => line.replace(LINE_MARKUP, ''))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (flat.length <= QUOTE_LABEL_MAX) return flat;
  const cut = flat.slice(0, QUOTE_LABEL_MAX);
  const boundary = cut.lastIndexOf(' ');
  return `${(boundary > QUOTE_LABEL_MAX / 2 ? cut.slice(0, boundary) : cut).trimEnd()}…`;
}

/**
 * Re-attaching something already staged is a mis-click rather than a second copy — unless it
 * arrives with a note, which is new information even when the context isn't, so the note lands
 * on the entry that's already there.
 */
function restage(selections: Selection[], match: Selection, note: string): { selections: Selection[] } {
  if (!note || note === match.note) return { selections };
  return { selections: selections.map((s) => (s.id === match.id ? { ...s, note } : s)) };
}

/**
 * Context picked outside the composer, waiting to be attached to the next message.
 *
 * A store rather than props because the picking end lives in a different subtree from the
 * composer — `PreviewTab`, `CodeViewer` and `DiffView` write from inside the side panel,
 * `PlanSessionPage` from the plan pane beside the thread, `Composer` reads from below it — and
 * none of them is an ancestor of the others.
 */
export const useSelectionStore = create<SelectionState>((set) => ({
  selections: [],
  annotating: null,

  addElement: (selection, annotate = false) =>
    set((state) => {
      const note = selection.note?.trim() ?? '';
      const match = state.selections.find(
        (s) => s.kind === 'element' && s.selector === selection.selector && s.route === selection.route,
      );
      // Re-attaching what's already staged still points the note field at it: the second
      // attempt is the user going back to say something about it, not a duplicate.
      if (match) return { ...restage(state.selections, match, note), annotating: annotate ? match.id : null };
      const id = crypto.randomUUID();
      return {
        selections: [...state.selections, { kind: 'element', ...selection, note, id }],
        annotating: annotate ? id : null,
      };
    }),
  addCode: (selection, annotate = false) =>
    set((state) => {
      const id = crypto.randomUUID();
      return {
        selections: [
          ...state.selections,
          {
            kind: 'code',
            ...selection,
            note: selection.note?.trim() ?? '',
            label: codeLabel(selection.path, selection.startLine, selection.endLine),
            id,
          },
        ],
        annotating: annotate ? id : null,
      };
    }),
  addFile: (selection, annotate = false) =>
    set((state) => {
      const note = selection.note?.trim() ?? '';
      // Already referenced whole — attaching it again would just repeat the same line.
      const match = state.selections.find((s) => s.kind === 'file' && s.path === selection.path);
      if (match) return { ...restage(state.selections, match, note), annotating: annotate ? match.id : null };
      const id = crypto.randomUUID();
      return {
        selections: [
          ...state.selections,
          { kind: 'file', ...selection, note, label: selection.path.split('/').pop() ?? selection.path, id },
        ],
        annotating: annotate ? id : null,
      };
    }),
  addQuote: (selection, annotate = false) =>
    set((state) => {
      const snippet = selection.snippet.trim();
      if (!snippet) return { selections: state.selections };
      const note = selection.note?.trim() ?? '';
      // Re-selecting the same words is a mis-drag, not a second quote.
      const match = state.selections.find(
        (s) => s.kind === 'quote' && s.snippet === snippet && s.role === selection.role,
      );
      if (match) return { ...restage(state.selections, match, note), annotating: annotate ? match.id : null };
      const id = crypto.randomUUID();
      return {
        selections: [
          ...state.selections,
          { kind: 'quote', ...selection, snippet, note, label: quoteLabel(snippet), id },
        ],
        annotating: annotate ? id : null,
      };
    }),
  addPlan: (selection, annotate = false) =>
    set((state) => {
      const snippet = selection.snippet.trim();
      if (!snippet) return { selections: state.selections };
      const note = selection.note?.trim() ?? '';
      // Re-selecting the same passage of the same version is a mis-drag, not a second comment.
      const match = state.selections.find(
        (s) => s.kind === 'plan' && s.snippet === snippet && s.version === selection.version,
      );
      if (match) return { ...restage(state.selections, match, note), annotating: annotate ? match.id : null };
      const id = crypto.randomUUID();
      return {
        selections: [
          ...state.selections,
          { kind: 'plan', ...selection, snippet, note, label: quoteLabel(snippet), id },
        ],
        annotating: annotate ? id : null,
      };
    }),
  setNote: (id, note) =>
    set((state) => ({
      selections: state.selections.map((s) => (s.id === id ? { ...s, note: note.trim() } : s)),
    })),
  setAnnotating: (id) => set({ annotating: id }),
  remove: (id) =>
    set((state) => ({
      selections: state.selections.filter((s) => s.id !== id),
      annotating: state.annotating === id ? null : state.annotating,
    })),
  clear: () => set({ selections: [], annotating: null }),
}));

const ELEMENT_FENCE_OPEN = '<selected-elements>';
const ELEMENT_FENCE_CLOSE = '</selected-elements>';
const REQUEST_MARKER = 'Request:';
const QUOTE_TAG = 'quoted-message';
const PLAN_TAG = 'plan-passage';
const NOTE_TAG = 'my-note'; // not-a-class: the XML tag wrapping a note in the composed prompt

/**
 * The user's own words about the block they follow, tagged so the agent can't mistake them for
 * part of the quoted or extracted material they sit next to.
 *
 * Deliberately joined with a single newline by every caller: element blocks are split apart on
 * blank lines when the thread reads the message back, so a note has to stay welded to the block
 * it annotates rather than becoming a paragraph of its own.
 */
function noteBlock(note: string): string {
  return note ? `\n<${NOTE_TAG}>\n${note}\n</${NOTE_TAG}>` : '';
}

/** `<filepath>`+`<content>` pair Claude Code itself uses for a file/code mention, so code and
 * whole-file context reads the same way here as it would if the user had typed `@file` there. */
function codeMention(filepath: string, content: string): string {
  return [
    '<code_mention>',
    `<filepath>${filepath}</filepath>`,
    '<content>',
    content,
    '</content>',
    '</code_mention>',
  ].join('\n');
}

/** A line-ref suffix (`:10` or `:10-20`) marks a mention as a snippet rather than a whole file —
 * the one bit `parseComposedMessage` needs back to sort a mention into the right chip. */
const LINE_REF = /:\d+(?:-\d+)?<\/filepath>/;

/**
 * Within a block the ordering is deliberate: the source location and the owning React component
 * come first when the dev build gave us them, because they are the only fields that point at a
 * file. The selector, ancestor path and markup are the fallback for finding it by hand.
 */
function elementBlock(s: ElementSelection, index: number): string {
  const heading = s.component ? `<${s.component}> (${s.label})` : s.label;
  const lines = [`${index + 1}. ${heading} on route \`${s.route}\``];

  if (s.source) lines.push(`   source: ${s.source}`);
  if (s.componentStack.length > 1) lines.push(`   components: ${s.componentStack.join(' < ')}`);
  lines.push(`   selector: ${s.selector}`);
  lines.push(`   dom path: ${s.path}`);
  if (s.attributes) lines.push(`   attributes: ${s.attributes}`);
  if (s.text) lines.push(`   text: ${JSON.stringify(s.text)}`);
  lines.push(`   box: ${s.rect.width}×${s.rect.height} at (${s.rect.x}, ${s.rect.y})`);
  lines.push('   html:', '   ```html', ...s.snippet.split('\n').map((l) => `   ${l}`), '   ```');

  return lines.join('\n') + noteBlock(s.note);
}

function codeBlock(s: CodeSelection): string {
  const filepath = s.startLine === s.endLine ? `${s.path}:${s.startLine}` : `${s.path}:${s.startLine}-${s.endLine}`;
  return codeMention(filepath, s.snippet) + noteBlock(s.note);
}

function fileBlock(s: FileSelection): string {
  return ['<code_mention>', `<filepath>${s.path}</filepath>`, '</code_mention>'].join('\n') + noteBlock(s.note);
}

/** `from` in the agent's second person — the quote is either something it said ("you") or
 * something the user said ("me"), and which one changes how it should be read. */
function quoteBlock(s: QuoteSelection): string {
  return (
    [`<${QUOTE_TAG} from="${s.role === 'assistant' ? 'you' : 'me'}">`, s.snippet, `</${QUOTE_TAG}>`].join('\n') +
    noteBlock(s.note)
  );
}

/** A passage of the plan, tagged with the version it was taken from. */
function planBlock(s: PlanSelection): string {
  return [`<${PLAN_TAG} version="${s.version}">`, s.snippet, `</${PLAN_TAG}>`].join('\n') + noteBlock(s.note);
}

/**
 * The outgoing message for a prompt written against picked context — DOM elements from the live
 * preview, code spans from a file or diff, quoted slices of earlier turns, or any mix.
 *
 * Framing, not just concatenation. Prefixing raw context ahead of the prompt leaves the agent to
 * guess what it is for — the observed failure being that it reads as background noise and the
 * instruction gets applied somewhere else. So each kind of context is fenced under its own
 * lead-in sentence, the *user's* words come last under an explicit marker, and — for elements,
 * the one kind the raw data can't state itself — the instruction the data cannot convey is spelled
 * out: change the source that renders this, not the DOM. Code spans already carry their own
 * source path, so they need no such instruction.
 *
 * Quotes come first and carry their own trailing instruction, because they change the *mood* of
 * everything after them: a quoted question turns the prompt into an answer to it, which the agent
 * would otherwise have to infer from a reply that looks like a fresh, contextless instruction.
 *
 * Each piece of context can also carry a note — the "what about it" that attaching alone can't
 * say. A note sits with its own block rather than being pooled at the end, so which of three
 * quoted questions an answer belongs to is a matter of position and not of guesswork. Notes can
 * also be the whole message: answering two questions in place leaves nothing to type below, and
 * the closing lines say so instead of pointing at a request that isn't there.
 */
export function composeMessage(selections: Selection[], text: string): string {
  if (selections.length === 0) return text;

  const quotes = selections.filter((s): s is QuoteSelection => s.kind === 'quote');
  const passages = selections.filter((s): s is PlanSelection => s.kind === 'plan');
  const elements = selections.filter((s): s is ElementSelection => s.kind === 'element');
  const code = selections.filter((s): s is CodeSelection => s.kind === 'code');
  const files = selections.filter((s): s is FileSelection => s.kind === 'file');

  const request = text.trim();
  const notes = selections.filter((s) => s.note).length;
  /** How the closing lines refer to the user's words, wherever those words ended up. */
  const said = request ? 'My request below is' : notes === 1 ? 'My note above is' : 'My notes above are';

  const sections: string[] = [];

  // Plan passages lead, and carry their own closing instruction, because they change what the
  // whole message *is*: not a new task but a revision of a document that already exists. Marking
  // three passages and typing nothing has to still produce one coherent rewrite addressing all
  // three, which is exactly what the closing line asks for.
  if (passages.length > 0) {
    const noun = passages.length === 1 ? 'passage' : 'passages';
    const version = passages[0]!.version;
    const sameVersion = passages.every((p) => p.version === version);
    sections.push(
      [
        `I'm commenting on ${passages.length} ${noun} of the plan${sameVersion ? ` (v${version})` : ''}.`,
        '',
        passages.map(planBlock).join('\n\n'),
        '',
        `Address ${passages.length === 1 ? 'it' : 'all of them'} and save the revised plan to PLAN.md.`,
        'Make one revision covering everything below rather than a separate pass per comment, and',
        'leave the rest of the plan as it is.',
      ].join('\n'),
    );
  }

  if (quotes.length > 0) {
    const noun = quotes.length === 1 ? 'passage' : 'passages';
    const pronoun = quotes.length === 1 ? 'it' : 'them';
    sections.push(
      [
        `I'm quoting ${quotes.length} ${noun} from earlier in this conversation.`,
        '',
        quotes.map(quoteBlock).join('\n\n'),
        '',
        ...(request
          ? [
              `My request below responds to ${pronoun} — an answer, a clarification, or a correction. Read`,
              `${pronoun} as the thing I'm replying to, and carry on from there rather than starting over.`,
            ]
          : [
              `${said} my answer to ${pronoun}, written against the passage ${notes === 1 ? 'it belongs' : 'each belongs'} to.`,
              `Treat ${pronoun} as answered and carry on from there rather than starting over.`,
            ]),
      ].join('\n'),
    );
  }

  if (elements.length > 0) {
    const noun = elements.length === 1 ? 'element' : 'elements';
    sections.push(
      [
        `I selected ${elements.length} ${noun} in the live preview of the running app.`,
        '',
        ELEMENT_FENCE_OPEN,
        elements.map(elementBlock).join('\n\n'),
        ELEMENT_FENCE_CLOSE,
      ].join('\n'),
    );
  }

  if (code.length > 0) {
    const noun = code.length === 1 ? 'snippet' : 'snippets';
    const lead = sections.length > 0 ? 'I also selected' : 'I selected';
    sections.push([`${lead} ${code.length} code ${noun}.`, '', code.map(codeBlock).join('\n\n')].join('\n'));
  }

  if (files.length > 0) {
    const noun = files.length === 1 ? 'file' : 'files';
    const lead = sections.length > 0 ? 'I also referenced' : 'I referenced';
    sections.push([`${lead} ${files.length} whole ${noun}.`, '', files.map(fileBlock).join('\n\n')].join('\n'));
  }

  // Quotes already closed their own section; a message carrying nothing else needs no second
  // closing line pointing at files or elements that aren't there.
  const picked = elements.length + code.length + files.length;
  if (picked > 0) {
    const pronoun = picked === 1 ? 'it' : 'them';
    let closing: string;
    if (elements.length > 0) {
      closing = [
        `${said} about ${pronoun}. Work out which source file renders ${pronoun} (start from`,
        'the source location and component names above when present, otherwise search for the markup',
        'or text) and change that source — the details above only describe the rendered output.',
      ].join('\n');
    } else if (files.length === 0) {
      closing = `${said} about ${pronoun}, quoted directly from the file${code.length === 1 ? '' : 's'} referenced above.`;
    } else {
      const referenced = code.length + files.length;
      closing = `${said} about ${pronoun} — read the referenced file${referenced === 1 ? '' : 's'} above for context.`;
    }
    sections.push(closing);
  }

  // With nothing typed under the context, the notes are the message — a `Request:` marker over
  // an empty line would only read as an instruction the user forgot to write.
  if (!request) return sections.join('\n\n');
  return [...sections, `${REQUEST_MARKER}\n${request}`].join('\n\n');
}

function extractFence(content: string, open: string, close: string): { blocks: string[]; end: number } | null {
  const start = content.indexOf(open);
  if (start === -1) return null;
  const end = content.indexOf(close, start);
  if (end === -1) return null;
  return {
    blocks: content
      .slice(start + open.length, end)
      .trim()
      .split('\n\n'),
    end,
  };
}

/**
 * The note welded to a block's end by [`noteBlock`], as an optional tail of the block's own
 * pattern.
 *
 * Matching it as *part of* the block is the whole point: cut at the closing tag instead and the
 * comment survives only as an entry in a pool of comments, leaving the thread to show "two
 * passages" beside two unattributed sentences and no way to tell which belongs to which.
 */
const NOTE_SUFFIX = `(?:\\n<${NOTE_TAG}>\\n[\\s\\S]*?\\n</${NOTE_TAG}>)?`;
const NOTE_SUFFIX_RE = new RegExp(`\\n<${NOTE_TAG}>\\n([\\s\\S]*?)\\n</${NOTE_TAG}>$`);

const CODE_MENTION_RE = new RegExp(`<code_mention>[\\s\\S]*?</code_mention>${NOTE_SUFFIX}`, 'g');
const QUOTE_RE = new RegExp(`<${QUOTE_TAG}[^>]*>[\\s\\S]*?</${QUOTE_TAG}>${NOTE_SUFFIX}`, 'g');
const PLAN_RE = new RegExp(`<${PLAN_TAG}[^>]*>[\\s\\S]*?</${PLAN_TAG}>${NOTE_SUFFIX}`, 'g');

/** The prose inside a wrapper tag, which is all the user ever saw of it. */
const PLAN_BODY_RE = new RegExp(`^<${PLAN_TAG}[^>]*>\\n([\\s\\S]*)\\n</${PLAN_TAG}>$`);
const QUOTE_BODY_RE = new RegExp(`^<${QUOTE_TAG}[^>]*>\\n([\\s\\S]*)\\n</${QUOTE_TAG}>$`);
const FILEPATH_RE = /<filepath>([\s\S]*?)<\/filepath>/;

/**
 * One piece of context as the thread reads it back: what was picked, and what the user wrote
 * about it.
 *
 * Paired rather than pooled, because a comment on a document is meaningless without the passage
 * it points at — "too vague" beside three passages says nothing at all.
 */
export interface ComposedBlock {
  /** Ready to render: the passage or quote itself for prose, the block as sent for the rest. */
  body: string;
  /** One line naming what it points at — a file path, or the passage's opening words. */
  label: string;
  /** What the user wrote about it. Empty when they attached it bare. */
  note: string;
}

function splitNote(block: string): { body: string; note: string } {
  const match = NOTE_SUFFIX_RE.exec(block);
  return match ? { body: block.slice(0, match.index), note: match[1]! } : { body: block, note: '' };
}

/** A plan passage or a quoted turn: markdown lifted out of a rendered document, so the tag that
 *  framed it for the agent comes off, and it is named the same way its chip was. */
function readProse(raw: string, bodyRe: RegExp): ComposedBlock {
  const { body, note } = splitNote(raw);
  const prose = bodyRe.exec(body)?.[1] ?? body;
  return { body: prose, label: quoteLabel(prose), note };
}

/** A code or whole-file mention keeps its tags — they are the format the agent reads, and the
 *  chip shows it as such — and is named by the path it carries. */
function readMention(raw: string): ComposedBlock {
  const { body, note } = splitNote(raw);
  return { body, label: FILEPATH_RE.exec(body)?.[1] ?? '', note };
}

/** An element names itself on its first line, under the position number it was written with —
 *  which the thread numbers for itself, so it comes off. */
function readElement(raw: string): ComposedBlock {
  const { body, note } = splitNote(raw);
  return { body, label: (body.split('\n')[0] ?? '').replace(/^\d+\.\s*/, ''), note };
}

/** Quote blocks aren't wrapped in a single fence — each one stands alone, so they're read back
 * the same way code mentions are: every match, plus where the last one ended. */
function extractQuotes(content: string): { quotes: ComposedBlock[]; end: number } {
  const quotes: ComposedBlock[] = [];
  let end = -1;
  for (const match of content.matchAll(QUOTE_RE)) {
    quotes.push(readProse(match[0], QUOTE_BODY_RE));
    end = Math.max(end, match.index + match[0].length);
  }
  return { quotes, end };
}

/** Plan passages stand alone like quotes do, so they are read back the same way. */
function extractPlanPassages(content: string): { passages: ComposedBlock[]; end: number } {
  const passages: ComposedBlock[] = [];
  let end = -1;
  for (const match of content.matchAll(PLAN_RE)) {
    passages.push(readProse(match[0], PLAN_BODY_RE));
    end = Math.max(end, match.index + match[0].length);
  }
  return { passages, end };
}

/** Every `<code_mention>` block in the message, sorted into snippets vs. whole files by whether
 * its `<filepath>` carries a line-ref suffix — the one distinction the wire format doesn't spell
 * out directly, since both kinds render through the same tag. */
function extractCodeMentions(content: string): { code: ComposedBlock[]; files: ComposedBlock[]; end: number } {
  const code: ComposedBlock[] = [];
  const files: ComposedBlock[] = [];
  let end = -1;
  for (const match of content.matchAll(CODE_MENTION_RE)) {
    (LINE_REF.test(match[0]) ? code : files).push(readMention(match[0]));
    end = Math.max(end, match.index + match[0].length);
  }
  return { code, files, end };
}

/**
 * Undo [`composeMessage`] for display.
 *
 * The framing exists for the agent; in the thread it buries the one line the user actually
 * wrote. Rather than storing a second copy of the message, the bubble reads the sent text back
 * apart — there is exactly one producer of this shape, and the round trip is covered by tests.
 */
export interface ComposedMessage {
  /** What the user typed under the context. Empty when their notes were the whole message. */
  request: string;
  passages: ComposedBlock[];
  quotes: ComposedBlock[];
  elements: ComposedBlock[];
  code: ComposedBlock[];
  files: ComposedBlock[];
}

export function parseComposedMessage(content: string): ComposedMessage | null {
  const elementFence = extractFence(content, ELEMENT_FENCE_OPEN, ELEMENT_FENCE_CLOSE);
  const { code, files, end: mentionsEnd } = extractCodeMentions(content);
  const { quotes, end: quotesEnd } = extractQuotes(content);
  const { passages, end: passagesEnd } = extractPlanPassages(content);
  if (!elementFence && code.length === 0 && files.length === 0 && quotes.length === 0 && passages.length === 0) {
    return null;
  }

  const elements = elementFence?.blocks.map(readElement) ?? [];
  const lastFenceEnd = Math.max(elementFence?.end ?? -1, mentionsEnd, quotesEnd, passagesEnd);
  const requestIdx = content.lastIndexOf(`\n${REQUEST_MARKER}\n`);
  // No marker is legitimate only when the notes were the message. Anything else with no marker
  // is some other text that merely happens to contain one of these tags — leave it alone.
  if (requestIdx === -1) {
    const annotated = [...passages, ...quotes, ...elements, ...code, ...files].some((block) => block.note);
    return annotated ? { passages, quotes, elements, code, files, request: '' } : null;
  }
  if (requestIdx < lastFenceEnd) return null;

  return {
    passages,
    quotes,
    elements,
    code,
    files,
    request: content.slice(requestIdx + REQUEST_MARKER.length + 2),
  };
}
